import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AppShell } from "./components/layout/AppShell";
import { ThemeProvider } from "./components/layout/ThemeProvider";
import { LeadGateProvider } from "./components/LeadCaptureModal";
import { LeadGateError } from "./lib/api";
import { Landing } from "./pages/Landing";
import { Overview } from "./pages/Overview";
import { Production } from "./pages/Production";
import { QualityControl } from "./pages/QualityControl";
import { Materials } from "./pages/Materials";
import { Insights } from "./pages/Insights";
import { Guide } from "./pages/Guide";
import { Explorer } from "./pages/Explorer";
import { Formulations } from "./pages/Formulations";
import { RawMaterialsQA } from "./pages/RawMaterialsQA";
import { WasteAnalysis } from "./pages/WasteAnalysis";
import { DataAnalyst } from "./pages/DataAnalyst";
import { KnowledgeBase } from "./pages/KnowledgeBase";
import { SharedConversation } from "./pages/SharedConversation";

// Singleton ref for the lead gate trigger — set by LeadGateProvider
let leadGateTrigger: (() => void) | null = null;
export function setLeadGateTrigger(fn: (() => void) | null) {
  leadGateTrigger = fn;
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      if (error instanceof LeadGateError && leadGateTrigger) {
        leadGateTrigger();
      }
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: (failureCount, error) => {
        // Don't retry lead gate errors
        if (error instanceof LeadGateError) return false;
        return failureCount < 1;
      },
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="dark">
        <LeadGateProvider>
        <BrowserRouter>
          <Routes>
            {/* Landing page — outside AppShell */}
            <Route path="/" element={<Landing />} />

            {/* Shared conversations — public, outside AppShell */}
            <Route path="/shared/:token" element={<SharedConversation />} />

            {/* Dashboard — inside AppShell */}
            <Route path="/app" element={<AppShell />}>
              <Route index element={<Overview />} />
              <Route path="production" element={<Production />} />
              <Route path="quality" element={<QualityControl />} />
              <Route path="materials" element={<Materials />} />
              <Route path="insights" element={<Insights />} />
              <Route path="formulations" element={<Formulations />} />
              <Route path="waste" element={<WasteAnalysis />} />
              <Route path="raw-materials" element={<RawMaterialsQA />} />
              <Route path="guide" element={<Guide />} />
              <Route path="explore" element={<Explorer />} />
              <Route path="analyst" element={<DataAnalyst />} />
              <Route path="kb" element={<KnowledgeBase />} />
            </Route>

            {/* Legacy redirects */}
            <Route path="/production" element={<Navigate to="/app/production" replace />} />
            <Route path="/quality" element={<Navigate to="/app/quality" replace />} />
            <Route path="/materials" element={<Navigate to="/app/materials" replace />} />
            <Route path="/insights" element={<Navigate to="/app/insights" replace />} />
            <Route path="/formulations" element={<Navigate to="/app/formulations" replace />} />
            <Route path="/waste" element={<Navigate to="/app/waste" replace />} />
            <Route path="/raw-materials" element={<Navigate to="/app/raw-materials" replace />} />
            <Route path="/guide" element={<Navigate to="/app/guide" replace />} />
            <Route path="/explore" element={<Navigate to="/app/explore" replace />} />
            <Route path="/analyst" element={<Navigate to="/app/analyst" replace />} />
            <Route path="/kb" element={<Navigate to="/app/kb" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </LeadGateProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
